import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Zap, Phone, Mail, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteSection() {
  const [form, setForm] = useState({ name: "", phone: "", job: "", preferred_date: "" });
  const [sending, setSending] = useState(false);
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const pickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Photo is too big — 10MB max.");
      return;
    }
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  };

  const clearPhoto = () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhoto(null);
    setPhotoPreview(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const { data: saved } = await axios.post(`${API}/quote`, form);
      if (photo) {
        try {
          const fd = new FormData();
          fd.append("file", photo);
          await axios.post(`${API}/quote/${saved.id}/photo`, fd);
        } catch {
          toast.warning(
            "Quote saved but the photo didn't upload — we'll grab it off you on the call."
          );
        }
      }
      toast.success("Locked in — we'll call you back shortly.", {
        description: "Need it sooner? Call 0450 454 453.",
      });
      setForm({ name: "", phone: "", job: "", preferred_date: "" });
      clearPhoto();
    } catch {
      toast.error("Couldn't send it — give us a call on 0450 454 453.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-white/15 bg-[#0A0C10] px-4 py-3.5 text-base text-white placeholder:text-slate-600 outline-none transition-all duration-300 focus:border-[#0052FF] focus:shadow-[0_0_0_3px_rgba(0,82,255,0.25)]";

  return (
    <section id="quote" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="grid items-center gap-12 md:grid-cols-2 md:gap-16"
        >
          <div>
            <p className="mb-4 font-tech text-xs uppercase tracking-[0.3em] text-[#4D8DFF]">
              Quick quote
            </p>
            <h2 className="font-display text-4xl font-extrabold uppercase leading-[0.9] tracking-tight sm:text-6xl">
              Three fields.
              <br />
              <span className="text-[#0052FF]">Sixty seconds.</span>
            </h2>
            <p className="mt-6 max-w-md text-base text-slate-300 sm:text-lg">
              Tell us the job — we'll call you back with a sharp price. No forms the
              length of a pour, no waiting around.
            </p>
            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3 font-tech text-xs uppercase tracking-[0.2em] text-slate-500">
                <Zap className="h-4 w-4 text-[#0052FF]" />
                Same-day call back, every time
              </div>
              <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                <span className="text-slate-500">Rather talk?</span>
                <a
                  href="tel:0450454453"
                  data-testid="quote-phone-link"
                  className="inline-flex items-center gap-2 font-semibold text-white transition-colors hover:text-[#4D8DFF]"
                >
                  <Phone className="h-4 w-4 text-[#0052FF]" />
                  Andrew — 0450 454 453
                </a>
                <a
                  href="tel:0474422539"
                  data-testid="quote-phone-eli-link"
                  className="inline-flex items-center gap-2 font-semibold text-white transition-colors hover:text-[#4D8DFF]"
                >
                  <Phone className="h-4 w-4 text-[#0052FF]" />
                  Eli — 0474 422 539
                </a>
                <a
                  href="mailto:APpumping@hotmail.com"
                  data-testid="quote-email-link"
                  className="inline-flex items-center gap-2 font-semibold text-white transition-colors hover:text-[#4D8DFF]"
                >
                  <Mail className="h-4 w-4 text-[#0052FF]" />
                  APpumping@hotmail.com
                </a>
              </div>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="quote-form"
            className="border border-white/10 bg-[#121721] p-6 sm:p-10"
          >
            <div className="space-y-5">
              <div>
                <label
                  htmlFor="quote-name"
                  className="mb-2 block font-tech text-[11px] uppercase tracking-[0.25em] text-slate-400"
                >
                  Name / Company
                </label>
                <input
                  id="quote-name"
                  data-testid="quote-name-input"
                  className={inputCls}
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Sam — Sam's Slabs"
                  required
                  maxLength={200}
                />
              </div>
              <div>
                <label
                  htmlFor="quote-phone"
                  className="mb-2 block font-tech text-[11px] uppercase tracking-[0.25em] text-slate-400"
                >
                  Phone
                </label>
                <input
                  id="quote-phone"
                  data-testid="quote-phone-input"
                  type="tel"
                  className={inputCls}
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="0400 000 000"
                  required
                  maxLength={40}
                />
              </div>
              <div>
                <label
                  htmlFor="quote-date"
                  className="mb-2 block font-tech text-[11px] uppercase tracking-[0.25em] text-slate-400"
                >
                  When do you need it? — optional
                </label>
                <input
                  id="quote-date"
                  data-testid="quote-date-input"
                  type="date"
                  min={new Date().toISOString().split("T")[0]}
                  className={inputCls}
                  value={form.preferred_date}
                  onChange={update("preferred_date")}
                />
              </div>
              <div>
                <label
                  htmlFor="quote-job"
                  className="mb-2 block font-tech text-[11px] uppercase tracking-[0.25em] text-slate-400"
                >
                  The job — what, where, when
                </label>
                <textarea
                  id="quote-job"
                  data-testid="quote-job-input"
                  rows={3}
                  className={`${inputCls} resize-none`}
                  value={form.job}
                  onChange={update("job")}
                  placeholder="e.g. 12m³ driveway slab, next Thursday, around back of the house"
                  required
                  maxLength={2000}
                />
              </div>
              <div>
                <span className="mb-2 block font-tech text-[11px] uppercase tracking-[0.25em] text-slate-400">
                  Photo of the job — optional
                </span>
                {photoPreview ? (
                  <div className="relative" data-testid="quote-photo-preview">
                    <img
                      src={photoPreview}
                      alt="Job preview"
                      className="h-36 w-full border border-white/15 object-cover"
                    />
                    <button
                      type="button"
                      onClick={clearPhoto}
                      data-testid="quote-photo-remove"
                      className="absolute right-2 top-2 bg-[#0A0C10]/90 px-3 py-1.5 font-tech text-[11px] uppercase tracking-widest text-white transition-colors hover:text-red-400"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-white/20 bg-[#0A0C10]/60 px-4 py-4 text-sm text-slate-400 transition-colors hover:border-[#0052FF] hover:text-[#7EB0FF]">
                    <Camera className="h-4 w-4" />
                    Take or choose a photo
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid="quote-photo-input"
                      onChange={pickPhoto}
                    />
                  </label>
                )}
              </div>
              <button
                type="submit"
                data-testid="quote-form-submit"
                disabled={sending}
                className="group flex w-full items-center justify-center gap-3 bg-[#0052FF] px-8 py-4 font-display text-xl font-bold uppercase tracking-wider text-white transition-all duration-300 hover:bg-[#2E74FF] hover:shadow-[0_0_36px_rgba(0,82,255,0.6)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {sending ? "Locking it in..." : "Lock it in"}
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
}